import { Button, CircularProgress } from '@material-ui/core'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthRedirect } from '../../../../hooks/use-auth-redirect'
import { useMainPageInfoBlockStyles } from './main-page-info-block.styles'

const HERO_SLIDE_COUNT = 4

function heroSlideImageSrc(index: number): string {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  return `${base}/images/main-hero-slide/${index + 1}.jpg`
}

type HeroSlideCopy = { text: string }

export const MainPageInfoBlock: React.FC = () => {
  const classes = useMainPageInfoBlockStyles()
  const navigateTo = useNavigate()
  const { authRedirectHandle } = useAuthRedirect()
  const { t, i18n } = useTranslation()

  const slides = useMemo((): HeroSlideCopy[] => {
    const raw = t('mainPage.heroSlides', { returnObjects: true })
    return Array.isArray(raw) ? (raw as HeroSlideCopy[]) : []
  }, [t, i18n.language])

  const [slideBgsVisible, setSlideBgsVisible] = useState<boolean[]>(() =>
    Array(HERO_SLIDE_COUNT).fill(false)
  )
  const [heroLoaderMounted, setHeroLoaderMounted] = useState(true)

  useEffect(() => {
    const cancelled = { current: false }
    const mark = (i: number) => {
      if (cancelled.current) return
      setSlideBgsVisible((prev) => {
        const next = [...prev]
        next[i] = true
        return next
      })
    }
    Array.from({ length: HERO_SLIDE_COUNT }, (_, i) => {
      const img = new Image()
      img.onload = () => mark(i)
      img.onerror = () => mark(i)
      img.src = heroSlideImageSrc(i)
    })
    return () => {
      cancelled.current = true
    }
  }, [])

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({
        delay: 7000,
        stopOnInteraction: false,
      }),
    []
  )

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 24 }, [
    autoplayPlugin,
  ])

  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback((api: any) => {
    setSelectedIndex(api.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)
    return () => {
      if (!emblaApi) return
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const descText =
    slides[selectedIndex]?.text ?? t('mainPage.description')

  const heroReady = slideBgsVisible[0]

  const onHeroLoaderTransitionEnd = (
    e: React.TransitionEvent<HTMLDivElement>
  ) => {
    if (e.target !== e.currentTarget) return
    if (e.propertyName !== 'opacity' || !heroReady) return
    setHeroLoaderMounted(false)
  }

  useEffect(() => {
    if (!heroReady) setHeroLoaderMounted(true)
  }, [heroReady])

  return (
    <div className={classes.infoBlock}>
      <div className={classes.imageHolder}>
        <div className={classes.heroCarouselRoot}>
          <div className={classes.heroViewport} ref={emblaRef}>
            <div className={classes.heroContainer}>
              {Array.from({ length: HERO_SLIDE_COUNT }).map((_, slideIndex) => (
                <div className={classes.heroSlide} key={slideIndex}>
                  <div
                    className={[
                      classes.heroSlideBg,
                      slideBgsVisible[slideIndex] &&
                        classes.heroSlideBgVisible,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={
                      {
                        ['--hero-slide-bg' as string]: `url(${heroSlideImageSrc(
                          slideIndex
                        )})`,
                      } as React.CSSProperties
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={classes.overflow} />
        <div className={classes.heroVerticalSpacer} />
        <div className={classes.mainText}>
          <span className={classes.kicker}>{t('mainPage.hero.kicker')}</span>
          <h4 className={classes.mainTitle}>
            <span className={classes.inlineTextBackdrop}>
              {t('mainPage.title')}
            </span>
          </h4>
          <p
            className={classes.mainDesc}
            aria-live="polite"
            aria-atomic="true"
          >
            <span
              key={selectedIndex}
              className={classes.heroDescLine}
            >
              {descText}
            </span>
          </p>
          <div className={classes.heroActionsRow}>
            <div className={classes.mainBtnHolder}>
              <Button
                onClick={(e) => authRedirectHandle(e, '/donate', '/donate')}
                variant="contained"
                color="primary"
                style={{
                  fontWeight: 700,
                  textTransform: 'initial',
                }}
              >
                {t('mainPage.buttons.donate')}
              </Button>
              <Button
                onClick={() => {
                  navigateTo('/search')
                }}
                variant="contained"
                color="primary"
                className={classes.buyButton}
                style={{
                  fontWeight: 700,
                  textTransform: 'initial',
                }}
              >
                {t('mainPage.buttons.buy')}
              </Button>
            </div>
          </div>
        </div>
        <div className={classes.heroVerticalSpacer} />
        {heroLoaderMounted && (
          <div
            className={[
              classes.heroImageLoader,
              heroReady && classes.heroImageLoaderHidden,
            ]
              .filter(Boolean)
              .join(' ')}
            onTransitionEnd={onHeroLoaderTransitionEnd}
          >
            <CircularProgress color="primary" />
          </div>
        )}
      </div>
    </div>
  )
}
